import React from 'react';
import { JourneyEvaluation } from '../../types/journey';
import { useLanguage } from '../../context/LanguageContext';

interface DetailedEvaluationProps {
  value?: JourneyEvaluation;
  onChange: (evaluation: JourneyEvaluation) => void;
  errors: Record<string, string>;
  showForm: boolean;
  onToggleForm: (show: boolean) => void;
}

const getEvaluationFactors = (t: (key: string) => string) => [
  {
    key: 'idea' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorIdea'),
    icon: '💡',
    iconBg: 'linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)',
    description: t('journey.evaluationFactorIdeaDesc'),
    levels: [
      t('journey.evaluationFactorIdeaLevel0'),
      t('journey.evaluationFactorIdeaLevel1'),
      t('journey.evaluationFactorIdeaLevel2'),
      t('journey.evaluationFactorIdeaLevel3')
    ]
  },
  {
    key: 'skills' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorSkills'),
    icon: '⚙️',
    iconBg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    description: t('journey.evaluationFactorSkillsDesc'),
    levels: [
      t('journey.evaluationFactorSkillsLevel0'),
      t('journey.evaluationFactorSkillsLevel1'),
      t('journey.evaluationFactorSkillsLevel2'),
      t('journey.evaluationFactorSkillsLevel3')
    ]
  },
  {
    key: 'team' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorTeam'),
    icon: '👥',
    iconBg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    description: t('journey.evaluationFactorTeamDesc'),
    levels: [
      t('journey.evaluationFactorTeamLevel0'),
      t('journey.evaluationFactorTeamLevel1'),
      t('journey.evaluationFactorTeamLevel2'),
      t('journey.evaluationFactorTeamLevel3')
    ]
  },
  {
    key: 'funding' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorFunding'),
    icon: '💰',
    iconBg: 'linear-gradient(135deg, #a8edea 0%, #29b6f6 100%)',
    description: t('journey.evaluationFactorFundingDesc'),
    levels: [
      t('journey.evaluationFactorFundingLevel0'),
      t('journey.evaluationFactorFundingLevel1'),
      t('journey.evaluationFactorFundingLevel2'),
      t('journey.evaluationFactorFundingLevel3')
    ]
  },
  {
    key: 'market' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorMarket'),
    icon: '📍',
    iconBg: 'linear-gradient(135deg, #c3ec52 0%, #0ba360 100%)',
    description: t('journey.evaluationFactorMarketDesc'),
    levels: [
      t('journey.evaluationFactorMarketLevel0'),
      t('journey.evaluationFactorMarketLevel1'),
      t('journey.evaluationFactorMarketLevel2'),
      t('journey.evaluationFactorMarketLevel3')
    ]
  },
  {
    key: 'tech' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorTech'),
    icon: '💻',
    iconBg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    description: t('journey.evaluationFactorTechDesc'),
    levels: [
      t('journey.evaluationFactorTechLevel0'),
      t('journey.evaluationFactorTechLevel1'),
      t('journey.evaluationFactorTechLevel2'),
      t('journey.evaluationFactorTechLevel3')
    ]
  },
  {
    key: 'network' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorNetwork'),
    icon: '🔗',
    iconBg: 'linear-gradient(135deg, #a8caba 0%, #5d4e75 100%)',
    description: t('journey.evaluationFactorNetworkDesc'),
    levels: [
      t('journey.evaluationFactorNetworkLevel0'),
      t('journey.evaluationFactorNetworkLevel1'),
      t('journey.evaluationFactorNetworkLevel2'),
      t('journey.evaluationFactorNetworkLevel3')
    ]
  },
  {
    key: 'time' as keyof JourneyEvaluation,
    title: t('journey.evaluationFactorTime'),
    icon: '⏰',
    iconBg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    description: t('journey.evaluationFactorTimeDesc'),
    levels: [
      t('journey.evaluationFactorTimeLevel0'),
      t('journey.evaluationFactorTimeLevel1'),
      t('journey.evaluationFactorTimeLevel2'),
      t('journey.evaluationFactorTimeLevel3')
    ]
  }
];

const DetailedEvaluation: React.FC<DetailedEvaluationProps> = ({
  value,
  onChange,
  errors,
  showForm,
  onToggleForm
}) => {
  const { t } = useLanguage();
  const evaluationFactors = getEvaluationFactors(t);

  const handleFactorChange = (factor: keyof JourneyEvaluation, rating: number) => {
    const currentValue = value || {};
    
    const newEvaluation: JourneyEvaluation = {
      ...currentValue,
      [factor]: rating
    } as JourneyEvaluation;
    onChange(newEvaluation);
  };

  if (!showForm) {
    return (
      <div className="evaluation-prompt">
        <div className="prompt-content">
          <h4>{t('journey.personalizePromptTitle')}</h4>
          <p>
            {t('journey.personalizePromptDescription')}
          </p>
          <p className="prompt-note">
            {t('journey.personalizePromptNote')}
          </p>
        </div>
        
        <div className="prompt-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onToggleForm(true)}
          >
            {t('journey.personalizeButtonYes')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detailed-evaluation">
      <div className="evaluation-header">
        <h3>{t('journey.evaluationHeaderTitle')}</h3>
        <p className="evaluation-description">
          {t('journey.evaluationHeaderDescription')}
        </p>
        <button
          type="button"
          className="btn btn-text"
          onClick={() => onToggleForm(false)}
        >
          {t('journey.evaluationSkipButton')}
        </button>
      </div>

      <div className="evaluation-factors-grid">
        {evaluationFactors.map((factor) => {
          const isCompleted = value && factor.key in value && typeof value[factor.key] === 'number' && value[factor.key] >= 0 && value[factor.key] <= 3;
          return (
          <div 
            key={factor.key} 
            className={`evaluation-factor-card ${isCompleted ? 'has-selection' : ''}`}
          >
            <div className="factor-icon-container">
              <div 
                className={`factor-icon ${isCompleted ? 'completed-icon' : ''}`}
                style={{ background: factor.iconBg }}
              >
                <span className="factor-emoji">{factor.icon}</span>
              </div>
              {isCompleted && (
                <div className="completion-checkmark">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
              )}
            </div>
            
            <h5 className="factor-title">{factor.title}</h5>
            
            <div className="factor-rating-compact">
              <select
                value={value?.[factor.key] ?? ''}
                onChange={(e) => handleFactorChange(factor.key, parseInt(e.target.value))}
                className={`factor-select ${isCompleted ? 'selected' : ''}`}
              >
                <option value="">{t('journey.evaluationRatePrompt')}</option>
                {factor.levels.map((level, index) => (
                  <option key={index} value={index}>
                    {index}: {level}
                  </option>
                ))}
              </select>
            </div>
          </div>
          );
        })}
      </div>

      {Object.keys(errors).some(key => evaluationFactors.find(f => f.key === key)) && (
        <div className="evaluation-errors">
          {evaluationFactors.map((factor) => (
            errors[factor.key] && (
              <div key={factor.key} className="error-message">
                {errors[factor.key]}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default DetailedEvaluation;